import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO } from '../data/mock';
import { Truck, Clock, Package, PawPrint } from 'lucide-react';

const ShippingPage = () => {
  useEffect(() => {
    document.title = 'Shipping Information | Paw Supplies Group';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main>
        {/* Banner */}
        <section className="relative bg-gradient-to-r from-brand-cream to-brand-paper border-b border-brand-divider">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-orange-light/30 border border-brand-divider rounded-full text-brand-orange-dark text-xs font-semibold mb-4">
              <PawPrint className="w-3.5 h-3.5" />
              SWIFT DELIVERY
            </div>
            <h1 className="text-4xl md:text-5xl text-brand-brown font-extrabold mb-3" className-FONT-DISPLAY>
              Shipping Information
            </h1>
            <p className="text-brand-brown-light max-w-lg">
              Fast, free shipping across the United States.
            </p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Shipping Highlights */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-brand-cream border border-brand-divider rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-brand-orange-light/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Truck className="w-7 h-7 text-brand-orange-dark" />
              </div>
              <h3 className="font-bold text-brand-brown text-lg mb-2" className-FONT-DISPLAY>Free Shipping</h3>
              <p className="text-brand-brown-light text-sm">On every order, no minimum</p>
            </div>
            <div className="bg-brand-cream border border-brand-divider rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-brand-orange-light/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-brand-orange-dark" />
              </div>
              <h3 className="font-bold text-brand-brown text-lg mb-2" className-FONT-DISPLAY>2-4 Business Days</h3>
              <p className="text-brand-brown-light text-sm">Standard USPS delivery</p>
            </div>
            <div className="bg-brand-cream border border-brand-divider rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-brand-orange-light/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Package className="w-7 h-7 text-brand-orange-dark" />
              </div>
              <h3 className="font-bold text-brand-brown text-lg mb-2" className-FONT-DISPLAY>Order Tracking</h3>
              <p className="text-brand-brown-light text-sm">Track your shipment</p>
            </div>
          </div>

          {/* Shipping Details */}
          <div className="bg-brand-cream border border-brand-divider rounded-3xl p-8 md:p-12">
            <h2 className="text-2xl font-bold text-brand-brown mb-6" className-FONT-DISPLAY>Shipping Policy</h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="font-bold text-brand-brown text-lg mb-3" className-FONT-DISPLAY>Shipping Rates</h3>
                <div className="bg-white rounded-xl overflow-hidden border border-brand-divider">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-brand-divider bg-brand-cream">
                        <th className="text-left p-4 text-brand-brown font-semibold">Order Total</th>
                        <th className="text-left p-4 text-brand-brown font-semibold">Shipping Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="p-4 text-brand-brown">Every order</td>
                        <td className="p-4 text-brand-orange-dark font-semibold">FREE via USPS</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-brand-brown text-lg mb-3" className-FONT-DISPLAY>Delivery Times</h3>
                <p className="text-brand-brown-light mb-4">
                  Standard shipping typically takes 2-4 business days from the date of shipment. Please allow 1 business day for order processing before shipment.
                </p>
                <ul className="text-brand-brown-light space-y-2 list-disc list-inside">
                  <li>Orders placed before 2 PM EST ship same day</li>
                  <li>Orders placed after 2 PM EST ship next business day</li>
                  <li>No deliveries on weekends or holidays</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-brand-brown text-lg mb-3" className-FONT-DISPLAY>Shipping Carrier</h3>
                <p className="text-brand-brown-light">
                  We ship all orders via USPS (United States Postal Service). You will receive a tracking number via email once your order ships.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-brand-brown text-lg mb-3" className-FONT-DISPLAY>Shipping Restrictions</h3>
                <p className="text-brand-brown-light">
                  Currently, we only ship within the United States. We do not ship to APO/FPO addresses or international destinations at this time.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-brand-brown text-lg mb-3" className-FONT-DISPLAY>Questions?</h3>
                <p className="text-brand-brown-light mb-4">
                  If you have any questions about shipping, please contact our support team:
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-orange text-white font-semibold rounded-full hover:bg-brand-orange transition-colors"
                  >
                    {CONTACT_INFO.email}
                  </a>
                  <a 
                    href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-brand-brown font-semibold rounded-full hover:bg-brand-cream transition-colors border border-brand-divider"
                  >
                    {CONTACT_INFO.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ShippingPage;
